import type {
  AddOutcome, AddTeammateRequest, EditOutcome, IssuedPairingCode, PeopleRoster, PersonId, TeamMember,
} from "@don/wire";

import { calendarDateLabel, dayGroupLabel } from "../library/row.js";

/**
 * The Team table's rules, as arithmetic (epic 26-10-02-knowledge-v0, issue 212).
 *
 * Pure and outside any component, for the reason `row.ts` is: the rules worth asserting are
 * what a row *means*, and a rule inside a render function can only be asserted through a
 * browser. `team.test.ts` asserts each one.
 *
 * **The session count is read here and nowhere else in `src`** (the owner's P1 exception,
 * `docs/design/principles.md`): it is shown beside the last shared date, and nothing sorts,
 * ranks or compares by it. `p1-count.test.ts` holds that by a glob over `packages/console/src`.
 */

/**
 * Where the Team table's rows come from, and its actions go: `GET /people`,
 * `POST /people/{id}/pairing-codes`, `POST /people`, `POST /people/{id}/edit`,
 * `POST /people/{id}/deactivate` and `POST /people/{id}/reactivate` (or a test's stand-in).
 */
export interface TeamSource {
  /** Rejects when the read fails; the screen then shows the problem box. */
  read(): Promise<PeopleRoster>;
  /**
   * New pairing code (issue 214): mints a code for anyone not deactivated, the owner's own
   * row included. Cancels nothing. Rejects when the server refuses or does not answer; the
   * screen then shows the page Notice and changes nothing.
   */
  issueCode(personId: PersonId): Promise<IssuedPairingCode>;
  /**
   * Add teammate (issue 215): `added` with the new row and their first code, or
   * `email_on_team`. Rejects when the server refuses or does not answer; the dialog then shows
   * its Notice and keeps what was typed.
   */
  add(request: AddTeammateRequest): Promise<AddOutcome>;
  /**
   * Edit (issue 216): `edited` with the person's row as the server now reads it, or
   * `email_on_team`. Rejects when the server refuses or does not answer; the dialog then shows
   * its Notice and keeps what was typed.
   */
  edit(personId: PersonId, request: AddTeammateRequest): Promise<EditOutcome>;
  /**
   * Deactivate (issue 217): resolves once the server has signed out every computer of the
   * person and voided their codes. Rejects when the server refuses or does not answer; the
   * screen then shows the page Notice and changes nothing.
   */
  deactivate(personId: PersonId): Promise<void>;
  /**
   * Reactivate (issue 218): resolves with the fresh pairing code once the server has returned
   * the person to the team (their old computers stay signed out). Rejects when the server
   * refuses or does not answer; the screen then shows the page Notice and changes nothing.
   */
  reactivate(personId: PersonId): Promise<IssuedPairingCode>;
}

/** The eight statuses, in the PRD's rule order ("Console status mapping"). */
export type TeamStatusKind =
  | "deactivated"
  | "notsetup"
  | "pending"
  | "expired"
  | "signed-out"
  | "cant-record"
  | "permissions"
  | "active";

/** How a status is drawn: `PersonStateMark`'s five kinds (design-spec §5). */
export type TeamMark = "active" | "pending" | "problem" | "deactivated" | "notsetup";

export interface TeamStatus {
  readonly kind: TeamStatusKind;
  readonly word: string;
  readonly mark: TeamMark;
}

const STATUS: Readonly<Record<TeamStatusKind, TeamStatus>> = {
  deactivated: { kind: "deactivated", word: "Deactivated", mark: "deactivated" },
  notsetup: { kind: "notsetup", word: "Not set up", mark: "notsetup" },
  pending: { kind: "pending", word: "Pending invite", mark: "pending" },
  expired: { kind: "expired", word: "Expired", mark: "problem" },
  "signed-out": { kind: "signed-out", word: "Signed out", mark: "problem" },
  "cant-record": { kind: "cant-record", word: "Can't record", mark: "problem" },
  permissions: { kind: "permissions", word: "Permissions", mark: "problem" },
  active: { kind: "active", word: "Active", mark: "active" },
};

/**
 * A member's status: the first rule that matches wins (PRD "Console status mapping").
 *
 * 1. Deactivated. 2. The owner, not paired, never given a code: Not set up (only the owner
 * reaches it; every employee gets a code when added). 3. Not paired with a live code: Pending
 * invite. 4. Not paired, no live code: Expired. 5. Paired with no live device: Signed out.
 * 6. Reading `failed`: Can't record. 7. Reading `permission_denied`: Permissions.
 * 8. Otherwise Active.
 *
 * So a Signed out person who is given a new code stays Signed out (rule 5 before 3 cannot
 * apply: they are paired), and a deactivated person with a live code reads Deactivated.
 */
export function teamStatus(member: TeamMember): TeamStatus {
  if (member.status === "deactivated") return STATUS.deactivated;
  if (!member.paired) {
    if (member.role === "owner" && !member.code_ever_issued) return STATUS.notsetup;
    return member.live_code !== null ? STATUS.pending : STATUS.expired;
  }
  if (!member.has_live_device) return STATUS["signed-out"];
  if (member.capture_reading === "failed") return STATUS["cant-record"];
  if (member.capture_reading === "permission_denied") return STATUS.permissions;
  return STATUS.active;
}

/** A problem word's tooltip: the problem (shown bold), then the fix beneath (decision 26). */
export interface ProblemTip {
  readonly problem: string;
  readonly fix: string;
}

/**
 * The tooltip for a problem status (issue 213; design-spec §6 copy), or `null` for Active,
 * Pending invite, Deactivated and Not set up, which have none (decision 28).
 *
 * **First names, never pronouns** (PRD "Copy uses first names"): no pronoun is stored, so
 * each "she", "him" or "her" of the design copy is the person's first name, the first word of
 * `display_name`, and "his computer's settings" reads "that computer's settings". The fix
 * points at the row's ⋯; the tooltip itself holds no control (decision 8). `team.test.ts`
 * asserts each line.
 */
export function problemTip(member: TeamMember): ProblemTip | null {
  const first = member.display_name.trim().split(/\s+/)[0] || member.display_name;
  switch (teamStatus(member).kind) {
    case "expired":
      return { problem: `${first}'s code ran out before ${first} used it.`, fix: `Give ${first} a new pairing code from ⋯.` };
    case "permissions":
      return {
        problem: `Screen recording is off on ${first}'s computer.`,
        fix: `Ask ${first} to allow screen recording for the recorder in that computer's settings.`,
      };
    case "cant-record":
      return {
        problem: `The recorder can't capture on ${first}'s computer.`,
        fix: `Ask ${first} to try another computer, then give ${first} a new pairing code from ⋯.`,
      };
    case "signed-out":
      return {
        problem: `${first}'s recorder hasn't connected in 30 days.`,
        fix: `Give ${first} a new pairing code from ⋯ to set it up again.`,
      };
    default:
      return null;
  }
}

/** Admin for the owner, Employee for everyone else (design-spec §6). */
export function roleLabel(member: TeamMember): "Admin" | "Employee" {
  return member.role === "owner" ? "Admin" : "Employee";
}

/** The Recordings cell: the last shared date and "N total", or `null` for "–". */
export interface RecordingsLabel {
  readonly last: string;
  readonly total: string;
}

/**
 * "Today", "Yesterday" or "Sep 12, 2026", with "N total" beneath; `null` when nothing of
 * theirs has reached the owner (the cell then reads "–"). Days are the viewer's calendar days,
 * the calendar the Sessions day groups use.
 */
export function recordingsLabel(member: TeamMember, nowMs: number, timeZone?: string): RecordingsLabel | null {
  const at = member.last_recorded_at_ms;
  if (at === null || member.session_count === 0) return null;
  const group = dayGroupLabel(nowMs, at, timeZone);
  const last = group === "Today" || group === "Yesterday" ? group : calendarDateLabel(at, timeZone);
  return { last, total: `${member.session_count} total` };
}

const byName = (a: TeamMember, b: TeamMember) =>
  a.display_name.localeCompare(b.display_name, undefined, { sensitivity: "base" }) ||
  a.person_id.localeCompare(b.person_id);

/**
 * The table's order (decision 22): the owner first, then everyone current by name (locale
 * compare, case-insensitive), then deactivated people by name. Nothing else moves a row, so
 * the table reads the same on every load and no one is ranked.
 */
export function orderRoster(people: readonly TeamMember[]): TeamMember[] {
  const owner = people.filter((m) => m.role === "owner");
  const current = people.filter((m) => m.role !== "owner" && m.status !== "deactivated");
  const gone = people.filter((m) => m.role !== "owner" && m.status === "deactivated");
  return [...owner.sort(byName), ...current.sort(byName), ...gone.sort(byName)];
}

/** The first word of a display name: the copy names people by it (PRD "Copy uses first names"). */
export function firstName(member: Pick<TeamMember, "display_name">): string {
  return member.display_name.trim().split(/\s+/)[0] || member.display_name;
}

/** A row ⋯ menu item. */
export type RowAction = "new-code" | "edit" | "deactivate" | "reactivate";

/**
 * Each item's words (design-spec §6 "Menu"). A `Record` over `RowAction`, so an item added to
 * the type without its label is a type error rather than a mislabelled item (214-code-quality
 * P2-2).
 */
export const ROW_ACTION_LABEL: Readonly<Record<RowAction, string>> = {
  "new-code": "New pairing code",
  edit: "Edit",
  deactivate: "Deactivate",
  reactivate: "Reactivate",
};

/** The items drawn after a hairline rule (design-spec §2 "Row ⋯ and menu": Deactivate). */
export const ROW_ACTION_RULE_BEFORE: ReadonlySet<RowAction> = new Set(["deactivate"]);

/**
 * What a row's ⋯ offers, in order (design-spec §5 "Menus"; decision 28). New pairing code, Edit
 * (issue 216) and, after a rule, Deactivate (issue 217) on every teammate who is not
 * deactivated; **the owner's row offers only New pairing code**, whatever his status (no Edit:
 * his email is his Google sign-in; no Deactivate: he would lock himself out, and the server
 * refuses it too). **A deactivated row offers Reactivate only** (issue 218, decision 19).
 */
export function rowActions(member: TeamMember): RowAction[] {
  if (member.status === "deactivated") return ["reactivate"];
  if (member.role === "owner") return ["new-code"];
  return ["new-code", "edit", "deactivate"];
}

/** `Oct 9`: a code's expiry in the owner's time zone, with no year (design-spec §6). */
export function expiresLabel(expiresAtMs: number, timeZone?: string): string {
  const parts = new Intl.DateTimeFormat("en-US", { month: "short", day: "numeric", timeZone }).formatToParts(
    expiresAtMs,
  );
  const part = (type: string) => parts.find((p) => p.type === type)?.value ?? "";
  return `${part("month")} ${part("day")}`;
}

/**
 * The code panel's words (design-spec §6 "Code panel"): "Code for {Full name}", and who types
 * it in. The owner's own panel speaks to him ("Type this into the recorder to set it up.",
 * §11 D9); everyone else's names them by first name.
 */
export function codePanelCopy(
  member: Pick<TeamMember, "role" | "display_name">,
  issued: IssuedPairingCode,
  timeZone?: string,
): { title: string; sentence: string } {
  const who = member.role === "owner" ? "Type" : `${firstName(member)} types`;
  return {
    title: `Code for ${member.display_name}`,
    sentence: `${who} this into the recorder to set it up. Expires ${expiresLabel(issued.expires_at_ms, timeZone)}.`,
  };
}

/** The page Notice when a mint fails (design-spec §6 "Page-level failures", §11 D4). */
export function newCodeFailedCopy(member: TeamMember): string {
  return `Couldn't make a new code for ${firstName(member)}. Nothing was changed.`;
}

/**
 * The roster once a code has been minted for one person: their newest live code is the new
 * one, and a code has now been issued to them. Nothing else moves, so the status falls out of
 * `teamStatus` (decision 27): a Pending invite, Expired or Not set up row reads Pending invite
 * with the new code, because it is unpaired and now has a live code; an Active, Signed out,
 * Permissions or Can't record row keeps its word, because it is paired and rules 5 to 8
 * decide without reading the code. `team.test.ts` asserts each.
 */
export function withIssuedCode(roster: PeopleRoster, issued: IssuedPairingCode): PeopleRoster {
  return {
    people: roster.people.map((m) =>
      m.person_id === issued.person_id
        ? { ...m, code_ever_issued: true, live_code: { code: issued.code, expires_at_ms: issued.expires_at_ms } }
        : m,
    ),
  };
}

/** The live code printed under a Pending invite, or `null` (no live code, or one with no text). */
export function pendingCode(member: TeamMember): string | null {
  return teamStatus(member).kind === "pending" ? (member.live_code?.code ?? null) : null;
}
